//Copyright (c) 2022 Panshak Solomon

import React, { Suspense, lazy, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { BrowserRouter, Route, Switch, Redirect, useLocation } from "react-router-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CircularProgress from "@material-ui/core/CircularProgress";
import Home from "./components/Home/Home";
import NavBar from "./components/NavBar/NavBar";
import Login from "./components/Login/Login";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import Calendar from "./components/Meetings/Calendar";
import MeetingForm from "./components/Meetings/MeetingForm";
import PersistentPosMainScreen from "./PoS/PersistentPosMainScreen";
import StaffCommission from "./components/Staff/StaffCommission";
import './app.css';
import Subscription from "./components/SubscriptionComponent/Subscription";
import SuperAdminNavBar from "./components/NavBar/SuperAdminNavbar";
import { TimezoneProvider } from "./Context/TimezoneContext";
import { NavDrawerProvider, useNavDrawer } from "./Context/NavDrawerContext";
import { SubscriptionProvider } from "./Context/SubscriptionContext";
import useSessionKeepAlive from "./utils/useSessionKeepAlive";
import { useCompany } from "./Context/CompanyContext";
import PermissionRoute from "./components/PermissionRoute";
import { getConfirmation } from "./Context/ConfirmationContext";

// A SuperAdmin's chosen display timezone should follow them into the POS
// view too when they switch there (see SuperAdminNavbar.js's "Go to POS") -
// not just stay a SuperAdmin-panel-only convenience - so every date/time a
// SuperAdmin looks at reads consistently regardless of which shell they're
// currently in. `active` is only ever true for that one case: a regular
// store login never sets viewingPosAsSuperAdmin, so this renders as a plain
// no-op fragment for every real store session, and useTimezone() falls
// through to its normal local-browser-time default exactly as before.
const ConditionalTimezoneProvider = ({ active, children }) =>
  active ? <TimezoneProvider>{children}</TimezoneProvider> : <>{children}</>;

// Every page reached by *navigating* (as opposed to the app shell itself -
// NavBar/Header/Footer - or the entry screens shown before any navigation
// even happens - Login/Subscription/the persistent POS screen) used to be a
// static import here, so the very first page load pulled down and parsed
// one giant bundle containing all ~40 of them before anything could render.
// React.lazy splits each into its own chunk fetched on first visit (and
// cached by the browser after), so both the initial load and each route
// change only wait on the one small chunk actually needed.
const Invoice = lazy(() => import("./components/Invoice/Invoice"));
const Invoices = lazy(() => import("./components/Invoices/Invoices"));
const CreditInvoices = lazy(() => import("./components/Invoices/CreditInvoices"));
const InvoiceDetails = lazy(() => import("./components/InvoiceDetails/InvoiceDetails"));
const ClientList = lazy(() => import("./components/Clients/ClientList"));
const SupplierList = lazy(() => import("./components/Suppliers/SuppliersList"));
const StaffList = lazy(() => import("./components/Staff/StaffList"));
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const Settings = lazy(() => import("./components/Settings/Settings"));
const Forgot = lazy(() => import("./components/Password/Forgot"));
const Reset = lazy(() => import("./components/Password/Reset"));
const ItemsList = lazy(() => import("./components/Items/ItemsList"));
const Meetings = lazy(() => import("./components/Meetings/Meetings"));
const AdminLogin = lazy(() => import("./admin/adminLogin/adminLogin"));
const AdminDashboard = lazy(() => import("./admin/adminDashboard"));
const AdminProfiles = lazy(() => import("./admin/adminProfiles"));
const Stats = lazy(() => import("./admin/stats"));
const UserStats = lazy(() => import("./admin/userStats"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const StockDetails = lazy(() => import("./stock/StockDetails"));
const Purchase = lazy(() => import("./components/Purchase/Purchase"));
const PurchaseDetails = lazy(() => import("./components/PurchaseDetails/PurchaseDetails"));
const Purchases = lazy(() => import("./components/Purchases/Purchases"));
const StockAdjustment = lazy(() => import("./stock/StockAdjustment/StockAdjustment"));
const StockAdjustmentDetails = lazy(() => import("./stock/StockAdjustmentDetails/StockAdjustmentDetails"));
const StockAdjustments = lazy(() => import("./stock/StockAdjustments/StockAdjustments"));
const BulkStockAdjustment = lazy(() => import("./stock/BulkStockAdjustment"));
const HowItWorks = lazy(() => import("./components/HowItWorks/HowItWorks"));
const EditStaff = lazy(() => import("./components/Staff/EditStaff"));
const StaffStats = lazy(() => import("./admin/StaffStats"));
const Statement = lazy(() => import("./admin/adminLogin/Statement"));
const ReportsHub = lazy(() => import("./components/Reports/ReportsHub"));
const UnitsList = lazy(() => import("./components/Items/units/unitsList"));
const InvoiceDeleteHistory = lazy(() => import("./components/Invoices/InvoiceDeleteHistory"));
const PurchaseDeleteHistory = lazy(() => import("./components/Purchases/PurchaseDeleteHistory"));
const StockAdjustmentDeleteHistory = lazy(() => import("./stock/StockAdjustments/StockAdjustmentDeleteHistory"));
const BulkItemUpload = lazy(() => import("./components/Items/BulkItemUpload"));
const BulkItemEdit = lazy(() => import("./components/Items/BulkItemEdit"));
const BulkImageUpload = lazy(() => import("./components/Items/BulkImageUpload"));
const FuturePriceList = lazy(() => import("./components/Items/FuturePriceList"));
const FuturePriceListView = lazy(() => import("./components/Items/FuturePriceListView"));
const LowStockPage = lazy(() => import("./components/Dashboard/LowStockPage"));
const SupplierDues = lazy(() => import("./components/Suppliers/SupplierDues"));
const CashManagement = lazy(() => import("./components/CashManagement/CashManagement"));
const CompanyLogo = lazy(() => import("./components/CompanyLogo/CompanyLogo.js"));
const EmployeeMaster = lazy(() => import("./components/Employees/EmployeeMaster"));
const ShiftLogs = lazy(() => import("./components/ShiftLogs/ShiftLogs"));
const ProgramLogs = lazy(() => import("./components/ProgramLogs/ProgramLogs"));
const GoodsReturnManagement = lazy(() => import("./admin/returnManagement/GoodsReturnManagement"));

// Suspense fallback while a route's chunk is fetched - NavBar/Header/Footer
// stay mounted around this, so only the content area shows this briefly
// instead of the whole screen going blank.
function RouteLoader() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
      <CircularProgress style={{ color: "#ef7f1a" }} />
    </div>
  );
}

// import { requestFirebaseNotificationPermission } from './firebaseInit'

// import AdminOrders from './admin/adminOrders';

// Gives each transitioning route its own DOM node to animate via a plain
// ref, instead of react-transition-group's default findDOMNode lookup -
// <Switch> renders no wrapping element of its own for that to latch onto.
function AnimatedRoute({ children, ...transitionProps }) {
  const nodeRef = React.useRef(null);
  return (
    <CSSTransition nodeRef={nodeRef} {...transitionProps}>
      <div ref={nodeRef} className="route-frame">
        {children}
      </div>
    </CSSTransition>
  );
}

function NavBarGate({ user }) {
  if (!user) {
    return null;
  }
  return <NavBar />;
}

// The desktop navbar is `position: fixed`, so it does not reserve any space in
// the document flow. Flagging the body lets one global CSS rule inset every
// routed page by the navbar width instead of each page guessing its own margin.
function SidebarOffset({ user }) {
  const { desktopNavVisible } = useNavDrawer() || {};
  const location = useLocation();

  useEffect(() => {
    const sidebarVisible = Boolean(user) && desktopNavVisible !== false;
    document.body.classList.toggle("has-sidebar", sidebarVisible);
    return () => document.body.classList.remove("has-sidebar");
  }, [user, desktopNavVisible]);

  // The desktop rail's own bottom offset (see .navbar in index.css) needs
  // extra clearance only on POS, where FloatingFooter (the category strip)
  // stacks above the global Footer. Payment hides the category strip, so it
  // uses the same footer clearance as every other page.
  useEffect(() => {
    const isPosScreen =
      location.pathname.includes("/PosMainScreen") ||
      location.pathname.startsWith("/edit/invoice");
    const isPaymentView = location.pathname.endsWith("/payment");
    document.body.classList.toggle("pos-floating-footer", isPosScreen && !isPaymentView);
    return () => document.body.classList.remove("pos-floating-footer");
  }, [location.pathname]);

  return null;
}

// One shared toast host for the whole app - bottom-right, slide-in
// transition, auto-dismisses but pauses on hover so a message doesn't
// vanish mid-read, stacks newest-on-top when several fire in a row.
function AppToasts() {
  return (
    <ToastContainer
      position="bottom-right"
      autoClose={3000}
      newestOnTop
      pauseOnFocusLoss
      pauseOnHover
      closeOnClick
      draggable
      transition={Slide}
      theme="colored"
      limit={4}
    />
  );
}

function App() {
  // Redux's authData is only populated once a sign-in actually dispatches
  // AUTH this session (the store isn't rehydrated from localStorage on
  // boot - see providers.js) - so it's empty on a fresh page load even for
  // an already-logged-in visitor. localStorage is the fallback for that
  // case. Reading Redux first (via useSelector, so App properly re-renders
  // when it changes) is what lets a login navigate the user in via
  // history.push instead of a full reload - see Login.js.
  const authData = useSelector((state) => state.auth?.authData);
  // Bumped by the LOGOUT reducer. Selecting it is what makes a logout
  // re-render this component at all: on any session that has been refreshed
  // since login, authData is already null, so LOGOUT's `authData: null` is a
  // no-op react-redux bails out of - and without this the cleared
  // localStorage below would never be re-read, leaving the logged-in tree
  // mounted over a dead session.
  const sessionVersion = useSelector((state) => state.auth?.sessionVersion || 0);
  const cachedUser = useMemo(
    () => JSON.parse(localStorage.getItem("profile") || "null"),
    [authData, sessionVersion]
  );
  const user = authData || cachedUser;
  const isSuperAdmin = user?.result?.userType?.includes("superAdmin");
  // SuperAdmin-only "Go to POS" switch (SuperAdminNavbar.js sets this,
  // Header.js's "Go to SuperAdmin Panel" clears it - both via a hard
  // navigation, not a soft client-side toggle, so this plain localStorage
  // read is all that's needed here: it's only ever re-evaluated on a fresh
  // page load). A regular admin/user account never has this flag at all,
  // so this can't affect anyone but a superAdmin who explicitly switched.
  const viewingPosAsSuperAdmin = isSuperAdmin && localStorage.getItem("superAdminViewingPos") === "true";
  const { company, fetchCompany } = useCompany();

  // Store & business settings (currency, tax rates, entitlements like Hold KOT,
  // Shift Tracking, Goods Return, etc.) are fetched fresh from /company on every
  // active session without storing in localStorage, so changes are immediately
  // authoritative and consistent across all devices and users.
  useEffect(() => {
    if (user) {
      fetchCompany();
    }
  }, [user?.result?._id, fetchCompany]);
  // Keeps an in-use till signed in instead of dropping it at token expiry.
  useSessionKeepAlive();
  // requestFirebaseNotificationPermission()
  //   .then((firebaseToken) => {
  //     // eslint-disable-next-line no-console
  //     console.log(firebaseToken);
  //   })
  //   .catch((err) => {/edit/invoice/
  //     return err;
  //   });
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && savedLanguage !== 'en') {
      const applyLanguage = (attempts = 5) => {
        const selectElement = document.querySelector('.goog-te-combo');
        if (selectElement) {
          selectElement.value = savedLanguage;
          selectElement.dispatchEvent(new Event('change'));
        } else if (attempts > 0) {
          setTimeout(() => applyLanguage(attempts - 1), 500);
        }
      };
      applyLanguage();
    }
  }, []);

  if (user && isSuperAdmin && !viewingPosAsSuperAdmin) {
    return (
      <>
        <AppToasts />
        <div>
          <BrowserRouter getUserConfirmation={getConfirmation}>
            {/* Wraps both the navbar's expiry chip and the Subscription tab's
                Extend panel - see SubscriptionContext.js for why. */}
            <SubscriptionProvider>
              {/* SuperAdmin-only display timezone (default India) - see
                  Context/TimezoneContext.js. Scoped to just this branch so a
                  regular store's own session (and its own use of the shared
                  ProgramLogs.js page) is never affected. */}
              <TimezoneProvider>
                <SuperAdminNavBar />
                <Route
                  render={({ location }) => (
                    <TransitionGroup className="route-transition-group">
                      {/* Fixed key (not location.pathname) - this branch is one
                          persistent shell with its own internal sub-navigation
                          (/superadmin, /superadmin/users, etc., see
                          Subscription.js), not a series of distinct pages. Keying
                          on the full pathname would fade-remount the whole shell
                          (losing sidebar collapse state, double-transitioning
                          against Subscription.js's own Grow) on every click. */}
                      <AnimatedRoute
                        key="superadmin-shell"
                        classNames="page-fade"
                        timeout={{ enter: 90, exit: 0 }}
                        unmountOnExit
                      >
                        <Switch location={location}>
                          <Route path="/superadmin" component={Subscription} />
                          {/* A superAdmin session is already authenticated - /login,
                              /, /Subscribers (the old path this lived at), and
                              anything else go straight to the dashboard instead of
                              showing the login form again under the app bar. */}
                          <Redirect to="/superadmin" />
                        </Switch>
                      </AnimatedRoute>
                    </TransitionGroup>
                  )}
                />
              </TimezoneProvider>
            </SubscriptionProvider>
          </BrowserRouter>
        </div>
      </>
    );
  }

  return (
    <>
      <AppToasts />
      <div className={user ? "app-shell" : undefined}>
        <BrowserRouter getUserConfirmation={getConfirmation}>
          <ConditionalTimezoneProvider active={viewingPosAsSuperAdmin}>
          <NavDrawerProvider>
            <SidebarOffset user={user} />
            <NavBarGate user={user} />
            <Header />
          <Route
            render={({ location }) => (
              <TransitionGroup className="route-transition-group">
                <AnimatedRoute
                  key={location.pathname}
                  classNames="page-fade"
                  timeout={{ enter: 90, exit: 0 }}
                  unmountOnExit
                >
                  <Suspense fallback={<RouteLoader />}>
                  <Switch location={location}>
            {/* Both of these used to render Login unconditionally, so an
                already-logged-in user landing on "/" or "/login" (a refresh,
                a bookmark, browser back) saw the login form rendered on top
                of the still-visible NavBar/Header, which check auth state
                separately and correctly stayed shown. */}
            <Route
              path="/login"
              exact
              render={() => (user ? <Redirect to="/PosMainScreen" /> : <Login />)}
            />
            <Route path="/forgot" exact component={Forgot} />
            <Route path="/reset/:token" exact component={Reset} />

            <Route
              path="/"
              exact
              render={() => (user ? <Redirect to="/PosMainScreen" /> : <Login />)}
            />
            {user && <Route path="/invoice" exact component={Invoice} />}
            {/* /edit/invoice/:id and /PosMainScreen (below) are handled by
                PersistentPosMainScreen, rendered outside this animated
                Switch so POS stays mounted across navigations instead of
                being torn down and rebuilt every visit - see that file. */}
            {user && (
              <PermissionRoute page="Invoices" path="/invoice/:id" exact component={InvoiceDetails} />
            )}
            {user && <PermissionRoute page="Invoices" path="/invoices" exact component={Invoices} />}
            {user && (
              <PermissionRoute page="Invoices" path="/invoices/credit" exact component={CreditInvoices} />
            )}
            {user && (
              <PermissionRoute
                page="Goods Returns"
                path="/goods-returns/:tab?"
                exact
                render={(props) =>
                  company?.goodsReturnEntitled === false ? (
                    <Redirect to="/" />
                  ) : (
                    <GoodsReturnManagement {...props} />
                  )
                }
              />
            )}
            {user && <Route path="/low-stock" exact component={LowStockPage} />}
            {user && (
              <PermissionRoute
                page="Invoices"
                path="/invoices/deleted"
                exact
                component={InvoiceDeleteHistory}
              />
            )}

            {user && (
              <PermissionRoute page="Stock Summary" path="/stockDetails" exact component={StockDetails} />
            )}

            {user && (
              <Route path="/reports/:tab?" exact component={ReportsHub} />
            )}

            {user && <PermissionRoute page="Purchase" path="/purchase" exact component={Purchase} />}
            {user && (
              <PermissionRoute page="Purchase" path="/edit/purchase/:id" exact component={Purchase} />
            )}
            {user && (
              <PermissionRoute page="Purchase" path="/purchase/:id" exact component={PurchaseDetails} />
            )}
            {user && <PermissionRoute page="Purchase" path="/purchases" exact component={Purchases} />}
            {user && (
              <PermissionRoute
                page="Purchase"
                path="/purchases/deleted"
                exact
                component={PurchaseDeleteHistory}
              />
            )}

            {user && (
              <PermissionRoute
                page="Stock Adjustment"
                path="/stockAdjustment"
                exact
                component={StockAdjustment}
              />
            )}
            {user && (
              <PermissionRoute
                page="Stock Adjustment"
                path="/edit/stockAdjustment/:id"
                exact
                component={StockAdjustment}
              />
            )}
            {user && (
              <PermissionRoute
                page="Stock Adjustment"
                path="/stockAdjustment/:id"
                exact
                component={StockAdjustmentDetails}
              />
            )}
            {user && (
              <PermissionRoute
                page="Stock Adjustment"
                path="/stockAdjustments/deleted"
                exact
                component={StockAdjustmentDeleteHistory}
              />
            )}
            {user && (
              <PermissionRoute
                page="Stock Adjustment"
                path="/stockAdjustments"
                exact
                component={StockAdjustments}
              />
            )}
            {user && (
              <PermissionRoute
                page="Stock Adjustment"
                path="/bulk-stock-adjustment"
                exact
                component={BulkStockAdjustment}
              />
            )}

            {user && <PermissionRoute adminOnly path="/admin" exact component={AdminLogin} />}
            {user && <PermissionRoute page="Settings" path="/settings" exact component={Settings} />}
            {user && <PermissionRoute page="Dashboard" path="/dashboard" exact component={Dashboard} />}
            {user && (
              <PermissionRoute adminOnly path="/adminDashboard" exact component={AdminDashboard} />
            )}
            {user && (
              <PermissionRoute adminOnly path="/adminProfiles" exact component={AdminProfiles} />
            )}

            {user && <Route path="/stats/:clientId" exact component={Stats} />}

            {user && (
              <Route path="/userStats/:userId" exact component={UserStats} />
            )}
            {user && (
              <Route path="/staffstats/:staffId" exact component={StaffStats} />
            )}
            {user && <PermissionRoute page="Payments & Receipts" path="/meetings" exact component={Meetings} />}
            {user && <Route path="/help" exact component={HowItWorks} />}
            {user && <PermissionRoute page="Customers" path="/customers" exact component={ClientList} />}
            {user && <PermissionRoute page="Suppliers" path="/suppliers" exact component={SupplierList} />}
            {user && <Route path="/cash-management" exact component={CashManagement} />}
            {user && (
              <PermissionRoute page="Supplier Dues" path="/supplier-dues" exact component={SupplierDues} />
            )}

            {user && (
              <PermissionRoute page="Staff" path="/edit/staff/:id" exact component={EditStaff} />
            )}
            {user && <Route path="/statement" exact component={Statement} />}
            {user && <PermissionRoute page="Staff" path="/staff" exact component={StaffList} />}
            {user && (
              <PermissionRoute
                page="Staff"
                path="/staff/commissionSummary"
                exact
                component={StaffList}
              />
            )}

            {user && <PermissionRoute page="Items" path="/ItemsList" exact component={ItemsList} />}
            {user && <PermissionRoute page="Units" path="/UnitsList" exact component={UnitsList} />}
            {user && (
              <PermissionRoute page="Items" path="/bulk-upload" exact component={BulkItemUpload} />
            )}
            {user && (
              <PermissionRoute page="Items" path="/bulk-edit">
                <BulkItemEdit />
              </PermissionRoute>
            )}
            {user && (
              <PermissionRoute
                page="Items"
                path="/bulk-image-upload"
                exact
                component={BulkImageUpload}
              />
            )}
            {user && (
              <PermissionRoute
                page="Items"
                exact
                path="/future-price-list"
                component={FuturePriceList}
              />
            )}
            {user && (
              <PermissionRoute
                page="Items"
                exact
                path="/future-price-list/view"
                component={FuturePriceListView}
              />
            )}

            <Route
              path="/Webview/PrivacyPolicy"
              exact
              component={PrivacyPolicy}
            />
              {user && (
              <Route path="/companyLogo" exact component={CompanyLogo} />
            )}
            {user && (
              <PermissionRoute
                page="Employee Master"
                path="/employees"
                exact
                render={(props) =>
                  company?.shiftTrackingEntitled === false || !company?.enableShiftTracking ? (
                    <Redirect to="/" />
                  ) : (
                    <EmployeeMaster {...props} />
                  )
                }
              />
            )}
            {user && (
              <PermissionRoute
                page="Shift Logs"
                path="/shift-logs"
                exact
                render={(props) =>
                  company?.shiftTrackingEntitled === false || !company?.enableShiftTracking ? (
                    <Redirect to="/" />
                  ) : (
                    <ShiftLogs {...props} />
                  )
                }
              />
            )}
            {user && (
              <Route
                path="/program-logs"
                exact
                render={(props) =>
                  !isSuperAdmin && company?.activityLogsVisible === false ? (
                    <Redirect to="/" />
                  ) : (
                    <ProgramLogs {...props} />
                  )
                }
              />
            )}
            {/* Every protected route above is only registered when `user`
                is set, so a logged-out visit to one (a stale bookmark, a
                reload after logout, browser back/forward) matched nothing
                and rendered a blank page - "/" already knows to show Login
                when logged out, so send it there instead of falling through
                to nothing. */}
            {!user && <Redirect to="/" />}
                  </Switch>
                  </Suspense>
                </AnimatedRoute>
              </TransitionGroup>
            )}
          />
          <PersistentPosMainScreen user={user} />
          <Footer />
          </NavDrawerProvider>
          </ConditionalTimezoneProvider>
        </BrowserRouter>
      </div>
    </>
  );
}

export default App;
